import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { EstablishmentFieldRequestDetailActions } from "../components/EstablishmentFieldRequestDetailActions";
import { EstablishmentFieldRequestDetailCards } from "../components/EstablishmentFieldRequestDetailCards";
import { EstablishmentFieldRequestDetailMap } from "../components/EstablishmentFieldRequestDetailMap";
import { EstablishmentFieldRequestRejectMotive } from "../components/EstablishmentFieldRequestRejectMotive";
import { EstablishmentFieldRequestStatus } from "@/constants/entity-status";
import { RequestTypes, TipoOperacionSolicitudEstablecimiento } from "@/constants/enums";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import { EstablishmentFieldRequestDetail } from "@/services/establishment-field-request.service";
import { Flex } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  confirmEstablishmentFieldRequestHandler,
  fetchEstablishmentFieldRequestDetailHandler,
  rejectEstablishmentFieldRequestHandler,
} from "./handler-functions";
import {
  OPERACION_LABEL,
  TIPO_CAMPO_LABELS,
} from "../schema/establishment-field-requests.schema";

export const EstablishmentsFieldsRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [detail, setDetail] = useState<EstablishmentFieldRequestDetail | null>(null);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openTipoCampoModal, setOpenTipoCampoModal] = useState(false);
  const [pendingTipoEstablecimiento, setPendingTipoEstablecimiento] = useState<number | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive && id) {
      fetchEstablishmentFieldRequestDetailHandler({
        id,
        setLoading,
        setDetail,
        navigate,
        fetchPermissions,
      });
    }
  }, [isActive, id]);

  const isLote = detail?.idTipoSolicitud === RequestTypes.SolicitudLote;
  const tipoLabel = isLote ? "lote" : "establecimiento";
  const operacionLabel = detail
    ? (OPERACION_LABEL[detail.tipoOperacionId] ?? detail.tipoOperacion.toLowerCase())
    : "operación";
  const isAltaEstablecimiento =
    !isLote && detail?.tipoOperacionId === TipoOperacionSolicitudEstablecimiento.Alta;

  const title = detail
    ? `Solicitud de ${OPERACION_LABEL[detail.tipoOperacionId] ?? detail.tipoOperacion.toLowerCase()} de ${tipoLabel}`
    : "Solicitud de establecimiento";

  const isPending = detail?.estado === EstablishmentFieldRequestStatus.PENDING;
  const isRejected = detail?.estado === EstablishmentFieldRequestStatus.REJECTED;
  const isBaja = detail?.tipoOperacionId === TipoOperacionSolicitudEstablecimiento.Baja;
  const canWrite = hasWriteAccess(PermissionCode.ESTABLISHMENTS_FIELDS_REQUESTS) ?? false;
  const anyLoading = loading || loadingConfirm || loadingReject;
  const disableConfirm = isBaja && !!detail?.haySolicitudesPendientes;

  const handleConfirm = async () => {
    if (!detail?.id) return;
    await confirmEstablishmentFieldRequestHandler({
      id: detail.id,
      setLoading: setLoadingConfirm,
      navigate,
      fetchPermissions,
      tipoLabel,
      operacionLabel,
      pendingTipoEstablecimiento,
    });
  };

  const handleReject = async (event?: FieldValues) => {
    if (!detail?.id) return;
    await rejectEstablishmentFieldRequestHandler({
      id: detail.id,
      event,
      setLoading: setLoadingReject,
      setOpenModal: setOpenRejectModal,
      navigate,
      fetchPermissions,
      tipoLabel,
      operacionLabel,
    });
  };

  const handleTipoAccept = async (situacion: number) => {
    setPendingTipoEstablecimiento(situacion);
    setOpenTipoCampoModal(false);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px" height="100%" minHeight={0}>
      <SectionHeader
        title={title}
        loading={anyLoading}
        aditionalButtons={
          <EstablishmentFieldRequestDetailActions
            isPending={isPending}
            canWrite={canWrite}
            anyLoading={anyLoading}
            disableConfirm={disableConfirm}
            onConfirm={handleConfirm}
            onReject={() => setOpenRejectModal(true)}
          />
        }
      />

      {!loading && detail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="16px"
          paddingRight="8px"
          paddingBottom="20px"
        >
          <EstablishmentFieldRequestDetailCards
            detail={detail}
            pendingTipoEstablecimiento={pendingTipoEstablecimiento}
            isPending={isPending}
            canWrite={canWrite}
            onOpenTipoCampoModal={() => setOpenTipoCampoModal(true)}
          />

          <EstablishmentFieldRequestDetailMap detail={detail} />

          {isRejected && detail.motivoRechazo && (
            <EstablishmentFieldRequestRejectMotive detail={detail} />
          )}
        </Flex>
      )}

      {detail?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleReject}
            loadingAccept={loadingReject}
            idTipoSolicitud={detail.idTipoSolicitud}
            entityName="solicitud"
          />
        </Suspense>
      )}

      {isAltaEstablecimiento && (
        <Suspense>
          <ModificarTipoCampoModalDynamic
            open={openTipoCampoModal}
            setOpen={setOpenTipoCampoModal}
            currentTipo={
              pendingTipoEstablecimiento != null
                ? TIPO_CAMPO_LABELS[pendingTipoEstablecimiento]
                : detail?.tipoEstablecimiento
            }
            loadingAccept={false}
            onAccept={handleTipoAccept}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((m) => ({
    default: m.RejectRequestModal,
  })),
);

const ModificarTipoCampoModalDynamic = lazy(() =>
  import("@/components/modal/ModificarTipoCampoModal").then((m) => ({
    default: m.ModificarTipoCampoModal,
  })),
);
